<script>
import storageService from "@/services/storage.service";
import axios from "axios";
import ApplicantLayout from "../../layouts/applicant";
import AppIcon from "@/components/shared/AppIcon.vue";
import { formatDate } from "@/helpers/dateFormatter";
import { STATUS_LABELS } from "@/constants/application.constants";

// What the banner (and the status chip) say for each application status.
const STATUS_BANNERS = {
  inprogress: {
    tone: "info",
    icon: "info",
    title: "Application in progress",
    text: "You have started your application but have not submitted it yet. Pick up where you left off.",
    cta: { label: "Continue application", to: "/membership_application" },
  },
  submitted: {
    tone: "success",
    icon: "check",
    title: "Thank you!",
    text: "Your application for membership has been submitted and is under review. Please log back into this portal for updates on the status of your application.",
  },
  in_review: {
    tone: "info",
    icon: "info",
    title: "Your application is being reviewed",
    text: "A member of our team is reviewing your application. We will notify you as soon as there is an update.",
  },
  incomplete: {
    tone: "warning",
    icon: "alert",
    title: "Action needed",
    text: "Your application was returned because some information is missing or needs correcting. Please review it and resubmit.",
    cta: { label: "Update application", to: "/membership_application_edit" },
  },
  verified: {
    tone: "info",
    icon: "check",
    title: "Your details have been verified",
    text: "Your identity and documents were verified. Your application is now waiting for final approval.",
  },
  approved: {
    tone: "success",
    icon: "check",
    title: "Congratulations!",
    text: "Your membership application has been approved. Welcome to Eastern Credit Union.",
  },
  rejected: {
    tone: "danger",
    icon: "x",
    title: "Application not approved",
    text: "Unfortunately your application was not approved. Please check the application log for details.",
  },
};

const CHIP_TONES = {
  inprogress: "",
  submitted: "orange",
  in_review: "info",
  incomplete: "warning",
  verified: "info",
  approved: "success",
  rejected: "danger",
};

const RECENT_LOG_LIMIT = 4;

export default {
  name: "ApplicantDashboard",
  components: { ApplicantLayout, AppIcon },
  data() {
    return {
      loading: true,
      loadError: false,
      status_first: "",
      full_name: "",
      Bank_Name: "",
      Date_now: "",
      Phone_no: "",
      Email: "",
      logs: [],
    };
  },
  computed: {
    banner() {
      return STATUS_BANNERS[this.status_first] || null;
    },
    statusLabel() {
      return STATUS_LABELS[this.status_first] || "";
    },
    statusTone() {
      return CHIP_TONES[this.status_first] || "";
    },
    infoRows() {
      return [
        { label: "Branch", icon: "bank", value: this.Bank_Name },
        { label: "Date", icon: "calendar", value: this.Date_now },
        { label: "Phone Number", icon: "phone", value: this.Phone_no },
        { label: "Email", icon: "mail", value: this.Email },
      ];
    },
    recentLogs() {
      return this.logs.slice(0, RECENT_LOG_LIMIT);
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.loading = true;
      this.loadError = false;
      axios
        .post("/applications/applicant_dashboard/", {
          token: storageService.getItem("accessToken"),
        })
        .then((response) => {
          const data = response.data || {};
          const userData = data.user_data || {};
          const userEmail = data.user_email || {};
          const a1 =
            typeof userData.section_a1 === "object" && userData.section_a1 !== null
              ? userData.section_a1
              : {};

          this.full_name =
            a1.first_name || a1.sur_name
              ? `${a1.first_name || ""} ${a1.sur_name || ""}`.trim()
              : `${userEmail.first_name || ""} ${userEmail.last_name || ""}`.trim() ||
                userEmail.username ||
                "N/A";
          this.Bank_Name = a1.branch || userEmail.branch_name || "N/A";
          this.Date_now = userData.created_on
            ? formatDate(userData.created_on)
            : a1.dob || "N/A";
          this.Phone_no = a1.home_contact || a1.mob_contact || userEmail.mob_no || "N/A";
          this.Email = userEmail.email || a1.email || "N/A";
          this.status_first = userData.status || "";
          this.logs = this.buildLogs(data.logs, userData);
        })
        .catch((error) => {
          console.error(error);
          this.loadError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Normalises the API logs (newest first). Falls back to a single synthetic entry
    // describing the current status when the backend has no log rows yet.
    buildLogs(rawLogs, userData) {
      const list = Array.isArray(rawLogs) ? rawLogs : [];
      if (list.length) {
        const mapped = list.map((l) => {
          const raw =
            l.created_on ||
            l.created_at ||
            (l.dateLog ? `${l.dateLog} ${l.timeLog || ""}` : "");
          return { ...l, dateLog: formatDate(raw), _ts: Date.parse(raw) };
        });
        if (mapped.every((l) => !isNaN(l._ts))) {
          mapped.sort((a, b) => b._ts - a._ts);
        }
        return mapped;
      }
      if (userData.id) {
        const activity = {
          incomplete: "Application marked as incomplete",
          approved: "Application approved",
          rejected: "Application rejected",
          verified: "Application verified",
        }[userData.status];
        return [
          {
            id: "initial",
            appactivity: activity || "Application submitted and under review",
            dateLog: userData.created_on ? formatDate(userData.created_on) : "Recent",
          },
        ];
      }
      return [];
    },
  },
};
</script>

<template>
  <ApplicantLayout>
    <section class="dash">
      <header class="ecu-page-head">
        <div>
          <h1>Dashboard</h1>
        </div>
      </header>

      <!-- status banner -->
      <transition name="dash-fade">
        <div v-if="banner && !loading" class="dash-banner" :class="`dash-banner--${banner.tone}`" role="status">
          <span class="dash-banner__icon"><AppIcon :name="banner.icon" :size="22" :stroke="2.6" /></span>
          <div class="dash-banner__body">
            <h2>{{ banner.title }}</h2>
            <p>{{ banner.text }}</p>
          </div>
          <router-link v-if="banner.cta" :to="banner.cta.to" class="ecu-btn-solid dash-banner__cta">
            {{ banner.cta.label }} <AppIcon name="arrow-right" :size="14" />
          </router-link>
        </div>
      </transition>

      <!-- error -->
      <div v-if="loadError" class="ecu-card">
        <div class="ecu-state">
          <span class="ecu-state__icon ecu-state__icon--danger"><AppIcon name="alert" :size="26" /></span>
          <h3>We couldn't load your dashboard</h3>
          <p>Please check your connection and try again.</p>
          <button type="button" class="ecu-btn-outline" @click="fetchData">
            <AppIcon name="refresh" :size="14" /> Try again
          </button>
        </div>
      </div>

      <div v-else class="dash__grid">
        <!-- profile card -->
        <article class="ecu-card">
          <template v-if="loading">
            <div class="ecu-card__head">
              <span class="ecu-skeleton" style="width: 46.4px; height: 46.4px; border-radius: 14.4px"></span>
              <span class="ecu-skeleton" style="width: 55%; height: 22.4px"></span>
            </div>
            <div v-for="n in 4" :key="n" class="ecu-skeleton" style="height: 41.6px; margin-bottom: 8px"></div>
          </template>
          <template v-else>
            <header class="ecu-card__head">
              <span class="ecu-tile ecu-tile--lg"><AppIcon name="id-card" :size="24" :stroke="1.6" /></span>
              <h2 class="ecu-card__title dash__name">{{ full_name }}</h2>
              <span v-if="statusLabel" class="ecu-chip" :class="statusTone && `ecu-chip--${statusTone}`">
                {{ statusLabel }}
              </span>
            </header>

            <ul class="dash-info">
              <li v-for="row in infoRows" :key="row.label">
                <span class="ecu-tile"><AppIcon :name="row.icon" :size="17" /></span>
                <span class="dash-info__label">{{ row.label }} :</span>
                <span class="dash-info__value">{{ row.value }}</span>
              </li>
            </ul>
          </template>
        </article>

        <!-- application log card -->
        <article class="ecu-card">
          <header class="ecu-card__head">
            <span class="ecu-tile ecu-tile--lg"><AppIcon name="file" :size="22" :stroke="1.6" /></span>
            <h2 class="ecu-card__title dash__logtitle">Application Log</h2>
            <router-link to="/view_log" class="ecu-btn-outline dash__more">
              View More <AppIcon name="arrow-right" :size="14" :stroke="2.2" />
            </router-link>
          </header>

          <template v-if="loading">
            <div class="ecu-skeleton" style="height: 73.6px"></div>
          </template>
          <ol v-else-if="recentLogs.length" class="dash-timeline">
            <li v-for="(log, idx) in recentLogs" :key="log.id || idx">
              <span class="dash-timeline__node"><AppIcon name="check" :size="12" :stroke="3" /></span>
              <div class="dash-timeline__card">
                <h3>{{ log.appactivity }}</h3>
                <p v-if="log.dateLog"><AppIcon name="clock" :size="14" /> {{ log.dateLog }}</p>
              </div>
            </li>
          </ol>
          <div v-else class="ecu-state">
            <span class="ecu-state__icon"><AppIcon name="inbox" :size="26" /></span>
            <p>No application activity recorded yet.</p>
          </div>
        </article>
      </div>
    </section>
  </ApplicantLayout>
</template>

<style scoped>
/* ---------- banner ---------- */
.dash-banner {
  --tone: #16a34a;
  --tone-bg: #e8f6ee;
  --tone-title: #0f8a3c;
  --tone-ring: rgba(22, 163, 74, 0.14);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 19.2px 25.6px;
  margin-bottom: 24px;
  border-radius: 17.6px;
  background: linear-gradient(100deg, var(--tone-bg) 0%, rgba(255, 255, 255, 0.55) 100%);
  border: 1px solid rgba(0, 0, 0, 0.03);
}
.dash-banner::after {
  /* soft wave in the corner, as in the design */
  content: "";
  position: absolute;
  right: -60px;
  bottom: -70px;
  width: 272px;
  height: 112px;
  border-radius: 50%;
  background: radial-gradient(closest-side, var(--tone), transparent);
  opacity: 0.13;
  pointer-events: none;
}
.dash-banner--info { --tone: #3b82f6; --tone-bg: #e8f0fe; --tone-title: #1d5fd1; --tone-ring: rgba(59, 130, 246, 0.14); }
.dash-banner--warning { --tone: #f59e0b; --tone-bg: #fff4de; --tone-title: #b86e00; --tone-ring: rgba(245, 158, 11, 0.16); }
.dash-banner--danger { --tone: #ef4444; --tone-bg: #fdecec; --tone-title: #c52222; --tone-ring: rgba(239, 68, 68, 0.14); }

.dash-banner__icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  background: var(--tone);
  box-shadow: 0 0 0 7.2px var(--tone-ring);
  margin: 0 6.4px 0 3.2px;
}
.dash-banner__body {
  flex: 1;
  min-width: 0;
}
.dash-banner__body h2 {
  margin: 0 0 2px;
  font-size: var(--ecu-fs-h2);
  font-weight: var(--ecu-fw-heading);
  letter-spacing: -0.01em;
  color: var(--tone-title);
}
.dash-banner__body p {
  margin: 0;
  font-size: var(--ecu-fs-body);
  line-height: 1.5;
  color: #1d2a4d;
}
.dash-banner__cta {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

/* ---------- grid ---------- */
.dash__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 25.6px;
  align-items: stretch;
}
.dash__name,
.dash__logtitle {
  flex: 1;
  min-width: 0;
}

/* ---------- info list ---------- */
.dash-info {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--ecu-border);
  border-radius: 12.8px;
  overflow: hidden;
}
.dash-info li {
  display: flex;
  align-items: center;
  gap: 11.2px;
  padding: 7.2px 11.2px;
}
.dash-info li + li {
  border-top: 1px solid var(--ecu-border);
}
.dash-info .ecu-tile {
  width: 32px;
  height: 32px;
  border-radius: 9.6px;
}
.dash-info__label {
  font-size: var(--ecu-fs-body);
  font-weight: var(--ecu-fw-normal);
  color: var(--ecu-navy);
  white-space: nowrap;
}
.dash-info__value {
  margin-left: auto;
  min-width: 0;
  text-align: right;
  font-size: var(--ecu-fs-body);
  font-weight: 500;
  color: var(--ecu-ink);
  overflow-wrap: anywhere;
}

/* ---------- timeline ---------- */
.dash-timeline {
  list-style: none;
  margin: 0;
  padding: 0;
}
.dash-timeline li {
  position: relative;
  display: grid;
  grid-template-columns: 27.2px minmax(0, 1fr);
  gap: 11.2px;
  padding-bottom: 11.2px;
}
.dash-timeline li:last-child {
  padding-bottom: 0;
}
.dash-timeline li:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 12.8px;
  top: 30.4px;
  bottom: -6px;
  width: 2px;
  border-radius: 2px;
  background: linear-gradient(180deg, #ff9a52, #ffd9bd);
}
.dash-timeline__node {
  width: 27.2px;
  height: 27.2px;
  margin-top: 12.8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #ff8a2a, #f2610f);
  box-shadow: 0 4.8px 11.2px rgba(242, 97, 15, 0.3);
  position: relative;
  z-index: 1;
}
.dash-timeline__card {
  padding: 12.8px 16px;
  border-radius: 12.8px;
  background: linear-gradient(100deg, #fff1ea, #fff7f2);
}
.dash-timeline__card h3 {
  margin: 0 0 6.4px;
  font-size: var(--ecu-fs-h3);
  font-weight: var(--ecu-fw-subheading);
  line-height: 1.3;
  color: var(--ecu-navy);
}
.dash-timeline__card p {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6.4px;
  font-size: var(--ecu-fs-body);
  color: #3c4668;
}

/* ---------- transitions / responsive ---------- */
.dash-fade-enter-active { transition: opacity 0.35s ease, transform 0.35s ease; }
.dash-fade-enter-from { opacity: 0; transform: translateY(4.8px); }

@media (max-width: 991px) {
  .dash__grid { grid-template-columns: 1fr; gap: 19.2px; }
}
@media (max-width: 767px) {
  .dash-banner { flex-wrap: wrap; padding: 16px; gap: 11.2px; }
  .dash-banner__icon { width: 41.6px; height: 41.6px; margin: 0 4.8px 0 0; }
  .dash-banner__body { flex-basis: calc(100% - 72px); }
  .dash-banner__body h2 { font-size: var(--ecu-fs-h3); }
  .dash-banner__body p { font-size: var(--ecu-fs-body); }
  .dash-banner__cta { width: 100%; justify-content: center; }
  .dash-info li { flex-wrap: wrap; }
  .dash-info__value { margin-left: 43.2px; flex-basis: 100%; text-align: left; }
}
@media (max-width: 575px) {
  .dash .ecu-card__head { flex-wrap: wrap; }
  .dash__more { margin-left: auto; }
}
</style>